import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getCountryByCode } from "@/data/countryData";
import { getCountryFromPath } from "@/data/countryDataResolver";
import { useGeoData } from "@/hooks/useGeoData";

const LOGO_URL = "/logo.png";

const socialLinks = [
  { name: "Instagram", url: "https://www.instagram.com/brandurbusinesss?igsh=MWxlanMzZzdtM2w3ZQ==", icon: "instagram" },
  { name: "Facebook", url: "https://www.facebook.com/share/1SbqqRd9to/", icon: "facebook" },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/brand-ur-business-a088563a1", icon: "linkedin" },
];

function buildTopCombos(cities, industries) {
  if (!cities.length || !industries.length) return [];
  const combos = [];
  const max = Math.min(12, cities.length);
  for (let i = 0; i < max; i++) {
    const city = cities[i];
    const ind = industries[i % industries.length];
    combos.push({ city: city.id, industry: ind.id, label: `${ind.name} in ${city.city}` });
  }
  return combos;
}

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const countryCode = getCountryFromPath(location.pathname);
  const cp = (path) => `/${countryCode}${path}`;
  const { cityData, industryData } = useGeoData(countryCode);
  const topCombos = buildTopCombos(cityData, industryData);

  const serviceLinks = [
    { name: "Business Consultancy", path: cp("/services/business-consultancy") },
    { name: "Digital Marketing", path: cp("/services/digital-marketing") },
    { name: "SEO Services", path: cp("/services/seo-services") },
    { name: "Website Development", path: cp("/services/website-development") },
    { name: "Paid Advertising", path: cp("/services/paid-advertising") },
    { name: "Branding & Creative", path: cp("/services/branding-creative") },
    { name: "Marketing Strategy", path: cp("/services/marketing-strategy") },
    { name: "Legal & Compliance", path: cp("/legal") },
  ];

  const quickLinks = [
    { name: "About Us", path: cp("/about") },
    { name: "Industries", path: cp("/industries") },
    { name: "Pricing", path: cp("/pricing") },
    { name: "Blog", path: cp("/blog") },
    { name: "Contact", path: cp("/contact") },
  ];
  return (
    <footer data-testid="main-footer" className="bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Brand Ur Business" width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12 object-contain" />
              <div>
                <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Brand Ur Business</h3>
                <p className="text-xs text-zinc-500 tracking-wider uppercase">Business Made Simple</p>
              </div>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your Complete Business Growth Partner. Strategy-driven digital solutions for branding, marketing, advertising & high-performance websites across India.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((s) => (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all" data-testid={`footer-social-${s.icon}`}>
                  <SocialIcon type={s.icon} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{t("footer.ourServices")}</h4>
            <div className="space-y-2">
              {serviceLinks.map((l) => (
                <Link key={l.path} to={l.path} className="block text-sm text-zinc-400 hover:text-white transition-colors">{l.name}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{t("footer.quickLinks")}</h4>
            <div className="space-y-2">
              {quickLinks.map((l) => (
                <Link key={l.path} to={l.path} className="block text-sm text-zinc-400 hover:text-white transition-colors">{l.name}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{t("footer.contactUs")}</h4>
            <div className="space-y-3">
              <a href="mailto:sales.brandurbusiness@gmail.com" className="flex items-start gap-3 text-sm text-zinc-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-purple-400" />
                sales.brandurbusiness@gmail.com
              </a>
              <div className="flex items-start gap-3 text-sm text-zinc-400">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-purple-400" />
                174, Ground Floor, G.V. F Block, Dr. Mukherjee Nagar, Delhi - 110009
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Internal Links Mega Section */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h5 className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-3">{t("footer.weServeIn")}</h5>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {cityData.map(c => (
                  <Link key={c.id} to={cp(`/city/${c.id}`)} className="text-xs text-zinc-500 hover:text-white transition-colors">{c.city}</Link>
                ))}
                <Link to={cp("/cities")} className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors" data-testid="footer-all-cities-link">{t("footer.viewAllCities")} &rarr;</Link>
              </div>
            </div>
            <div>
              <h5 className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-3">{t("footer.industries")}</h5>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {industryData.map(i => (
                  <Link key={i.id} to={cp(`/industry/${i.id}`)} className="text-xs text-zinc-500 hover:text-white transition-colors">{i.name}</Link>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-3">{t("footer.popularServices")}</h5>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {topCombos.map((c, i) => (
                  <Link key={i} to={cp(`/city/${c.city}/${c.industry}`)} className="text-xs text-zinc-500 hover:text-white transition-colors">{c.label}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">
            &copy; {new Date().getFullYear()} Brand Ur Business. {t("footer.allRightsReserved")}.
          </p>
          <div className="flex gap-4 text-xs text-zinc-500">
            <Link to={cp("/contact")} className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ type }) {
  if (type === "instagram") return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  );
  if (type === "facebook") return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  );
  if (type === "linkedin") return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
  );
  return null;
}
